import React, { useState, useContext, useEffect, useMemo } from "react";
import { RoleContext } from "../../contexts/RoleContext";
import { AuthContext } from "../../contexts/AuthContext";
import { useMasters } from "../../contexts/MastersContext";
import { useDesignation } from "../../contexts/DesignationsContext";
import { useEmploymentType } from "../../contexts/EmploymentTypeMasterContext";
import apiClient from "../../utils/apiUtils";
import { AlertTriangle, Eye, EyeOff } from "lucide-react";

const UserForm = ({ closeModal, onSubmit, initialValues }) => {
  const roleContext = useContext(RoleContext);
  const { token } = useContext(AuthContext);
  const { departments } = useMasters();
  const { designations } = useDesignation();
  const { employmentTypes } = useEmploymentType();

  const roles = useMemo(() => roleContext?.roles || [], [roleContext?.roles]);
  const loadRoles = roleContext?.loadRoles;

  const [showPasswordSection] = useState(!initialValues);

  // Manager users grouped by role
  const [managerUsersByRole, setManagerUsersByRole] = useState({});
  const [loadingManagers, setLoadingManagers] = useState(false);

  const [formData, setFormData] = useState(() => {
    // Get role ID from initialValues
    let roleId = "";
    if (initialValues) {
      if (initialValues.role_id) {
        roleId = initialValues.role_id;
      } else if (initialValues.roles && initialValues.roles.length > 0) {
        roleId = initialValues.roles[0].id || "";
      } else if (initialValues.role) {
        roleId = initialValues.role;
      }
    }

    if (!initialValues) {
      return {
        first_name: "",
        middle_name: "",
        last_name: "",
        email: "",
        phone: "",
        date_of_birth: "",
        date_of_joining: "",
        department_id: "",
        designation_id: "",
        reporting_to: "",
        employment_type_id: "",
        gender: "",
        role: "",
        manager_id: "",
        secondary_approver_id: "",
        hod_department_id: "",
        password: "",
        password_confirmation: "",
      };
    }

    return {
      first_name:
        initialValues.first_name ||
        (initialValues.full_name || initialValues.name
          ? (initialValues.full_name || initialValues.name).split(" ")[0]
          : ""),
      middle_name: initialValues.middle_name || "",
      last_name:
        initialValues.last_name ||
        (initialValues.full_name || initialValues.name
          ? (initialValues.full_name || initialValues.name)
              .split(" ")
              .slice(1)
              .join(" ")
          : ""),
      email: initialValues.email || "",
      phone: initialValues.phone || "",
      date_of_birth: initialValues.date_of_birth || "",
      date_of_joining: initialValues.date_of_joining || "",
      department_id:
        initialValues.department_id || initialValues.department || "",
      designation_id:
        initialValues.designation_id || initialValues.designation || "",
      reporting_to: initialValues.reporting_to || "",
      employment_type_id:
        initialValues.employment_type_id || initialValues.employment_type || "",
      gender: initialValues.gender || "",
      role: roleId,
      manager_id: initialValues.manager_id || "",
      secondary_approver_id: initialValues.secondary_approver_id || "",
      hod_department_id: initialValues.hod_department_id || "",
      password: "",
      password_confirmation: "",
    };
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (token && loadRoles && typeof loadRoles === "function") {
      loadRoles(token);
    }
  }, [token, loadRoles]);

  // Fetch managers grouped by roles
  useEffect(() => {
    const fetchManagers = async () => {
      try {
        setLoadingManagers(true);
        const response = await apiClient.get("/users-by-roles");
        if (response.data && response.data.success) {
          setManagerUsersByRole(response.data.users || {});
        }
      } catch (error) {
        console.error("Error fetching managers:", error);
      } finally {
        setLoadingManagers(false);
      }
    };
    fetchManagers();
  }, []);

  // Effect to sync IDs when master data is loaded or initialValues change
  useEffect(() => {
    if (initialValues) {
      setFormData((prev) => {
        const updates = {};

        const findId = (list, currentVal, initialVal) => {
          if (!list || list.length === 0) return null;

          // If currentVal is already a valid ID in the list, no need to change
          if (list.some((item) => String(item.id) === String(currentVal)))
            return null;

          const valToFind = currentVal || initialVal;
          if (!valToFind) return null;

          const found = list.find(
            (item) =>
              String(item.name).toLowerCase() ===
                String(valToFind).toLowerCase() ||
              String(item.id) === String(valToFind),
          );
          return found ? found.id : null;
        };

        // Role
        const newRoleId = findId(roles, prev.role, initialValues.role);
        if (newRoleId && String(newRoleId) !== String(prev.role))
          updates.role = newRoleId;

        // Department
        const newDeptId = findId(
          departments,
          prev.department_id,
          initialValues.department,
        );
        if (newDeptId && String(newDeptId) !== String(prev.department_id))
          updates.department_id = newDeptId;

        // Designation
        const newDesigId = findId(
          designations,
          prev.designation_id,
          initialValues.designation,
        );
        if (newDesigId && String(newDesigId) !== String(prev.designation_id))
          updates.designation_id = newDesigId;

        // Employment Type
        const newEmpTypeId = findId(
          employmentTypes,
          prev.employment_type_id,
          initialValues.employment_type,
        );
        if (
          newEmpTypeId &&
          String(newEmpTypeId) !== String(prev.employment_type_id)
        )
          updates.employment_type_id = newEmpTypeId;

        if (Object.keys(updates).length > 0) {
          return { ...prev, ...updates };
        }
        return prev;
      });
    }
  }, [roles, departments, designations, employmentTypes, initialValues]);

  const validateName = (value, fieldName) => {
    if (!value || value.trim().length === 0) {
      return `${fieldName} is required.`;
    }
    if (value.length > 50) {
      return `${fieldName} must not exceed 50 characters.`;
    }
    if (!/^[A-Za-z\s]+$/.test(value)) {
      return `${fieldName} must contain only letters.`;
    }
    return "";
  };

  const validateEmail = (value) => {
    if (!value || value.trim().length === 0) {
      return "Email is required.";
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return "Invalid email format.";
    }
    return "";
  };

  const validatePhone = (value) => {
    if (!value || value.trim().length === 0) {
      return "Phone number is required.";
    }
    if (!/^\d{10}$/.test(value)) {
      return "Phone number must be exactly 10 digits.";
    }
    return "";
  };

  const validateDateOfBirth = (value) => {
    if (!value) return "Date of birth is required.";
    const birthDate = new Date(value);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    if (age < 18) {
      return "User must be at least 18 years old.";
    }
    return "";
  };

  const validatePassword = (value) => {
    if (!value || value.length === 0) return "Password is required.";
    if (value.length < 8) return "Password must be at least 8 characters.";
    if (!/(?=.*[A-Z])/.test(value))
      return "Password must contain at least one uppercase letter.";
    return "";
  };

  const validatePasswordConfirmation = (value, password) => {
    if (!value || value.length === 0) return "Confirm password is required.";
    if (value !== password) return "Passwords do not match.";
    return "";
  };

  const selectedRole = roles.find(
    (r) => String(r.id) === String(formData.role),
  );
  const roleName = selectedRole ? selectedRole.name : "";
  const isHOD = roleName.toUpperCase() === "HOD";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Real-time validation
    let error = "";
    if (["first_name", "last_name"].includes(name)) {
      error = validateName(
        value,
        name
          .split("_")
          .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
          .join(" "),
      );
    } else if (name === "middle_name") {
      error = value ? validateName(value, "Middle Name") : "";
    } else if (name === "email") {
      error = validateEmail(value);
    } else if (name === "phone") {
      error = validatePhone(value);
    } else if (name === "password" && showPasswordSection) {
      error = validatePassword(value);
    } else if (name === "password_confirmation" && showPasswordSection) {
      error = validatePasswordConfirmation(value, formData.password);
    } else if (name === "date_of_birth") {
      error = validateDateOfBirth(value);
    } else if (
      [
        "department_id",
        "designation_id",
        "employment_type_id",
        "gender",
        "role",
        "date_of_joining",
      ].includes(name)
    ) {
      error = !value
        ? `${name.replace("_id", "").replace(/_/g, " ")} is required.`
        : "";
    } else if (name === "hod_department_id" && isHOD) {
      error = !value ? "HOD Department is required." : "";
    }

    setFieldErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError("");

    const errors = {};
    errors.first_name = validateName(formData.first_name, "First Name");
    errors.middle_name = formData.middle_name
      ? validateName(formData.middle_name, "Middle Name")
      : "";
    errors.last_name = validateName(formData.last_name, "Last Name");
    errors.email = validateEmail(formData.email);
    errors.phone = validatePhone(formData.phone);
    errors.date_of_birth = validateDateOfBirth(formData.date_of_birth);
    errors.date_of_joining = !formData.date_of_joining
      ? "Date of joining is required."
      : "";
    errors.department_id = !formData.department_id
      ? "Department is required."
      : "";
    errors.designation_id = !formData.designation_id
      ? "Designation is required."
      : "";
    errors.employment_type_id = !formData.employment_type_id
      ? "Employee type is required."
      : "";
    errors.gender = !formData.gender ? "Gender is required." : "";
    errors.role = !formData.role ? "Role is required." : "";

    if (
      formData.manager_id &&
      formData.secondary_approver_id &&
      formData.manager_id === formData.secondary_approver_id
    ) {
      errors.secondary_approver_id =
        "Secondary approver cannot be the same as primary approver.";
    }

    if (isHOD) {
      errors.hod_department_id = !formData.hod_department_id
        ? "Department (as HOD) is required."
        : "";
    }

    if (showPasswordSection) {
      errors.password = validatePassword(formData.password);
      errors.password_confirmation = validatePasswordConfirmation(
        formData.password_confirmation,
        formData.password,
      );
    }

    setFieldErrors(errors);

    const hasErrors = Object.values(errors).some((err) => err !== "");
    if (hasErrors) return;

    setIsSubmitting(true);
    try {
      const submissionData = {
        first_name: formData.first_name,
        middle_name: formData.middle_name,
        last_name: formData.last_name,
        name: `${formData.first_name} ${formData.middle_name} ${formData.last_name}`
          .replace(/\s+/g, " ")
          .trim(),
        email: formData.email,
        phone: formData.phone,
        date_of_birth: formData.date_of_birth,
        gender: formData.gender,
        date_of_joining: formData.date_of_joining,
        department_id: formData.department_id
          ? parseInt(formData.department_id)
          : null,
        designation_id: formData.designation_id
          ? parseInt(formData.designation_id)
          : null,
        employment_type_id: formData.employment_type_id
          ? parseInt(formData.employment_type_id)
          : null,
        manager_id: formData.manager_id ? parseInt(formData.manager_id) : null,
        secondary_approver_id: formData.secondary_approver_id
          ? parseInt(formData.secondary_approver_id)
          : null,
        role: roleName,
      };

      if (isHOD) {
        submissionData.hod_department_id = formData.hod_department_id
          ? parseInt(formData.hod_department_id)
          : null;
      }

      if (showPasswordSection) {
        submissionData.password = formData.password;
        submissionData.password_confirmation = formData.password_confirmation;
      }

      const result = await onSubmit(submissionData);
      if (result && !result.success) {
        setApiError(
          result.message || "Something went wrong. Please try again.",
        );
      }
    } catch (err) {
      setApiError(
        "A connection error occurred. Please check your internet and try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      autoComplete="off"
      className="space-y-4 font-poppins px-2"
    >
      {apiError && (
        <div className="p-3 bg-red-50 border-l-4 border-red-500 rounded flex items-center gap-3 mb-4 animate-in fade-in slide-in-from-top-2 duration-300">
          <AlertTriangle className="text-red-500 shrink-0" size={18} />
          <p className="text-sm text-red-700 font-medium">{apiError}</p>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Names */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-white">
            First Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="first_name"
            value={formData.first_name}
            onChange={handleChange}
            className={`mt-1 block w-full p-2 border rounded-md dark:bg-black dark:text-white ${fieldErrors.first_name ? "border-red-500" : "border-gray-300"}`}
            required
          />
          {fieldErrors.first_name && (
            <p className="text-xs text-red-500 mt-1">
              {fieldErrors.first_name}
            </p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-white">
            Middle Name
          </label>
          <input
            type="text"
            name="middle_name"
            value={formData.middle_name}
            onChange={handleChange}
            className={`mt-1 block w-full p-2 border rounded-md dark:bg-black dark:text-white ${fieldErrors.middle_name ? "border-red-500" : "border-gray-300"}`}
          />
          {fieldErrors.middle_name && (
            <p className="text-xs text-red-500 mt-1">
              {fieldErrors.middle_name}
            </p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-white">
            Last Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="last_name"
            value={formData.last_name}
            onChange={handleChange}
            className={`mt-1 block w-full p-2 border rounded-md dark:bg-black dark:text-white ${fieldErrors.last_name ? "border-red-500" : "border-gray-300"}`}
            required
          />
          {fieldErrors.last_name && (
            <p className="text-xs text-red-500 mt-1">{fieldErrors.last_name}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Email & Phone */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-white">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`mt-1 block w-full p-2 border rounded-md dark:bg-black dark:text-white ${fieldErrors.email ? "border-red-500" : "border-gray-300"}`}
            required
            autoComplete="off"
          />
          {fieldErrors.email && (
            <p className="text-xs text-red-500 mt-1">{fieldErrors.email}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-white">
            Phone <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className={`mt-1 block w-full p-2 border rounded-md dark:bg-black dark:text-white ${fieldErrors.phone ? "border-red-500" : "border-gray-300"}`}
            required
          />
          {fieldErrors.phone && (
            <p className="text-xs text-red-500 mt-1">{fieldErrors.phone}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* DOB & DOJ */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-white">
            Date of Birth <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            name="date_of_birth"
            value={formData.date_of_birth}
            onChange={handleChange}
            max={
              new Date(new Date().setFullYear(new Date().getFullYear() - 18))
                .toISOString()
                .split("T")[0]
            }
            className={`mt-1 block w-full p-2 border rounded-md dark:bg-black dark:text-white ${fieldErrors.date_of_birth ? "border-red-500" : "border-gray-300"}`}
            required
          />
          {fieldErrors.date_of_birth && (
            <p className="text-xs text-red-500 mt-1">
              {fieldErrors.date_of_birth}
            </p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-white">
            Date of Joining <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            name="date_of_joining"
            value={formData.date_of_joining}
            onChange={handleChange}
            className={`mt-1 block w-full p-2 border rounded-md dark:bg-black dark:text-white ${fieldErrors.date_of_joining ? "border-red-500" : "border-gray-300"}`}
            required
          />
          {fieldErrors.date_of_joining && (
            <p className="text-xs text-red-500 mt-1">
              {fieldErrors.date_of_joining}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Department & Designation */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-white">
            Department <span className="text-red-500">*</span>
          </label>
          <select
            name="department_id"
            value={formData.department_id}
            onChange={handleChange}
            className={`mt-1 block w-full p-2 border rounded-md dark:bg-black dark:text-white ${fieldErrors.department_id ? "border-red-500" : "border-gray-300"}`}
            required
          >
            <option value="">Select Department</option>
            {departments.map((dept) => (
              <option key={dept.id} value={dept.id}>
                {dept.name}
              </option>
            ))}
          </select>
          {fieldErrors.department_id && (
            <p className="text-xs text-red-500 mt-1">
              {fieldErrors.department_id}
            </p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-white">
            Designation <span className="text-red-500">*</span>
          </label>
          <select
            name="designation_id"
            value={formData.designation_id}
            onChange={handleChange}
            className={`mt-1 block w-full p-2 border rounded-md dark:bg-black dark:text-white ${fieldErrors.designation_id ? "border-red-500" : "border-gray-300"}`}
            required
          >
            <option value="">Select Designation</option>
            {designations.map((desig) => (
              <option key={desig.id} value={desig.id}>
                {desig.name}
              </option>
            ))}
          </select>
          {fieldErrors.designation_id && (
            <p className="text-xs text-red-500 mt-1">
              {fieldErrors.designation_id}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Approvers */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-white">
            Primary Approver (Reporting To)
          </label>
          <select
            name="manager_id"
            value={formData.manager_id}
            onChange={(e) => {
              const selectedId = e.target.value;
              setFormData((prev) => ({
                ...prev,
                manager_id: selectedId,
                reporting_to: selectedId,
              }));
              if (selectedId && selectedId === formData.secondary_approver_id) {
                setFieldErrors((prev) => ({
                  ...prev,
                  secondary_approver_id:
                    "Secondary approver cannot be the same as primary approver.",
                }));
              } else {
                setFieldErrors((prev) => ({
                  ...prev,
                  secondary_approver_id: "",
                }));
              }
            }}
            className="mt-1 block w-full p-2 border border-gray-300 rounded-md dark:bg-black dark:text-white focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">
              {loadingManagers ? "Loading..." : "Select Primary Approver"}
            </option>
            {Object.entries(managerUsersByRole).map(([role, list]) => (
              <optgroup key={role} label={role}>
                {list.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-white">
            Secondary Approver
          </label>
          <select
            name="secondary_approver_id"
            value={formData.secondary_approver_id}
            onChange={(e) => {
              const selectedId = e.target.value;
              setFormData((prev) => ({
                ...prev,
                secondary_approver_id: selectedId,
              }));
              if (selectedId && selectedId === formData.manager_id) {
                setFieldErrors((prev) => ({
                  ...prev,
                  secondary_approver_id:
                    "Secondary approver cannot be the same as primary approver.",
                }));
              } else {
                setFieldErrors((prev) => ({
                  ...prev,
                  secondary_approver_id: "",
                }));
              }
            }}
            className={`mt-1 block w-full p-2 border rounded-md dark:bg-black dark:text-white focus:ring-blue-500 focus:border-blue-500 ${fieldErrors.secondary_approver_id ? "border-red-500" : "border-gray-300"}`}
          >
            <option value="">
              {loadingManagers ? "Loading..." : "Select Secondary Approver"}
            </option>
            {Object.entries(managerUsersByRole).map(([role, list]) => (
              <optgroup key={role} label={role}>
                {list.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          {fieldErrors.secondary_approver_id && (
            <p className="text-xs text-red-500 mt-1">
              {fieldErrors.secondary_approver_id}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Employee Type & Gender */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-white">
            Employee Type <span className="text-red-500">*</span>
          </label>
          <select
            name="employment_type_id"
            value={formData.employment_type_id}
            onChange={handleChange}
            className={`mt-1 block w-full p-2 border rounded-md dark:bg-black dark:text-white ${fieldErrors.employment_type_id ? "border-red-500" : "border-gray-300"}`}
            required
          >
            <option value="">Select Employee Type</option>
            {employmentTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
          {fieldErrors.employment_type_id && (
            <p className="text-xs text-red-500 mt-1">
              {fieldErrors.employment_type_id}
            </p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-white">
            Gender <span className="text-red-500">*</span>
          </label>
          <select
            name="gender"
            value={formData.gender}
            onChange={handleChange}
            className={`mt-1 block w-full p-2 border rounded-md dark:bg-black dark:text-white ${fieldErrors.gender ? "border-red-500" : "border-gray-300"}`}
            required
          >
            <option value="">Select Gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
          {fieldErrors.gender && (
            <p className="text-xs text-red-500 mt-1">{fieldErrors.gender}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Role & HOD Dept */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-white">
            Role <span className="text-red-500">*</span>
          </label>
          <select
            name="role"
            value={formData.role}
            onChange={handleChange}
            className={`mt-1 block w-full p-2 border rounded-md dark:bg-black dark:text-white ${fieldErrors.role ? "border-red-500" : "border-gray-300"}`}
            required
          >
            <option value="">Select Role</option>
            {roles.map((role) => (
              <option key={role.id} value={role.id}>
                {role.name}
              </option>
            ))}
          </select>
          {fieldErrors.role && (
            <p className="text-xs text-red-500 mt-1">{fieldErrors.role}</p>
          )}
        </div>
        {isHOD && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white">
              Department (as HOD) <span className="text-red-500">*</span>
            </label>
            <select
              name="hod_department_id"
              value={formData.hod_department_id}
              onChange={handleChange}
              className={`mt-1 block w-full p-2 border rounded-md dark:bg-black dark:text-white ${fieldErrors.hod_department_id ? "border-red-500" : "border-gray-300"}`}
              required={isHOD}
            >
              <option value="">Select Department</option>
              {departments.map((dept) => (
                <option key={dept.id} value={dept.id}>
                  {dept.name}
                </option>
              ))}
            </select>
            {fieldErrors.hod_department_id && (
              <p className="text-xs text-red-500 mt-1">
                {fieldErrors.hod_department_id}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Password Section */}
      {showPasswordSection && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white">
              Password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={`mt-1 block w-full p-2 pr-10 border rounded-md dark:bg-black dark:text-white shadow-sm focus:ring-blue-500 focus:border-blue-500 ${fieldErrors.password ? "border-red-500" : "border-gray-300"}`}
                required={showPasswordSection}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {fieldErrors.password && (
              <p className="text-xs text-red-500 mt-1">
                {fieldErrors.password}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white">
              Confirm Password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                name="password_confirmation"
                value={formData.password_confirmation}
                onChange={handleChange}
                className={`mt-1 block w-full p-2 pr-10 border rounded-md dark:bg-black dark:text-white shadow-sm focus:ring-blue-500 focus:border-blue-500 ${fieldErrors.password_confirmation ? "border-red-500" : "border-gray-300"}`}
                required={showPasswordSection}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {fieldErrors.password_confirmation && (
              <p className="text-xs text-red-500 mt-1">
                {fieldErrors.password_confirmation}
              </p>
            )}
          </div>
        </div>
      )}

      <div className="flex justify-end items-center gap-2 pt-4">
        <button
          type="button"
          onClick={closeModal}
          className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900 font-medium transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting
            ? initialValues
              ? "Updating..."
              : "Adding..."
            : initialValues
              ? "Update User"
              : "Add User"}
        </button>
      </div>
    </form>
  );
};

export default UserForm;
